import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';

import get from 'lodash/get';

import {
    DISPLAY_MODE_STANDARD,
    DISPLAY_MODE_PANEL,
    DISPLAY_MODE_FULL_SCREEN,
    DISPLAY_MODE_FORM_INLINE,
    DISPLAY_MODE_WIDTH,
    DEFAULT_FORM_WIDTH,
    FULL_WIDTH_FORM,
    FORM_CONTENT_WIDTH,
    DEFAULT_FORM_PADDING,
} from 'common/entities/form/styleConfiguration/constants';
import {
    getFormColors,
    getFormBackgroundImage,
    getFormDisplayStyles,
    getFormLinksStyles,
} from 'common/entities/form/styleConfiguration/selectors';
import {
    DEFAULT_FORM_LINKS_COLOR,
    DEFAULT_FORM_LINKS_COLOR_HOVER,
    DEFAULT_FORM_LINKS_COLOR_FOCUS,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { colorAppBackground } from 'common/styles/colors';
import { baseBorderRadius, containerPaddingM } from 'common/styles/variables';
import FormBanner from './FormBanner';
import FormCloseButton from './FormCloseButton';
import { withFormId } from '../FormIdContext';

/**
 * Implementation of visual layout of the form.
 */
const FormBodyView = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    position: relative;
    overflow: auto;
    height: 100%;
    width: ${(props) => DISPLAY_MODE_WIDTH[props.displayMode]};
    max-width: 100%;
    background-color: ${(props) => (props.backgroundColor && props.backgroundColor.value) || colorAppBackground};
    border-radius: ${baseBorderRadius};

    background-image: ${(props) => (props.backgroundImageUrl ? `url("${props.backgroundImageUrl}")` : 'none')};
    background-size: cover;
    background-position: center center;

    @media (max-width: ${DEFAULT_FORM_WIDTH}) {
        width: ${FULL_WIDTH_FORM};
    }

    a {
        color: ${(props) => props.linksColor};
    }
    a:hover {
        color: ${(props) => props.linksColorHover};
    }
    a:focus {
        color: ${(props) => props.linksColorFocus};
    }
`;

const FormContentView = styled.div`
    padding: ${containerPaddingM};
    width: ${(props) => FORM_CONTENT_WIDTH[props.displayMode]};
    max-width: 100%;

    /* desktop */
    @media (min-width: 667px) {
        padding: ${DEFAULT_FORM_PADDING};
    }
    @media (max-width: ${DEFAULT_FORM_WIDTH}) {
        width: 100%;
    }
`;

const BASE_COMPONENT_NAME = 'FormView';

const FormView = ({
    backgroundColor,
    backgroundImageUrl,
    linksColor,
    linksColorHover,
    linksColorFocus,
    displayMode,
    onRequestClose,
    isInline,
    children,
}) => {
    const formDisplay = isInline ? DISPLAY_MODE_FORM_INLINE : displayMode;

    return (
        <FormBodyView
            data-qa={`${BASE_COMPONENT_NAME}__container`}
            className={`${BASE_COMPONENT_NAME}__container`}
            backgroundColor={backgroundColor}
            backgroundImageUrl={backgroundImageUrl}
            displayMode={formDisplay}
            linksColor={linksColor}
            linksColorHover={linksColorHover}
            linksColorFocus={linksColorFocus}
        >
            {onRequestClose && <FormCloseButton onRequestClose={onRequestClose} />}
            <FormBanner />
            <FormContentView className={`${BASE_COMPONENT_NAME}__FormContentView`} displayMode={formDisplay}>
                {children}
            </FormContentView>
        </FormBodyView>
    );
};

FormView.propTypes = {
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.array]).isRequired,
    backgroundColor: PropTypes.object,
    backgroundImageUrl: PropTypes.string,
    displayMode: PropTypes.oneOf([DISPLAY_MODE_STANDARD, DISPLAY_MODE_PANEL, DISPLAY_MODE_FULL_SCREEN]),
    onRequestClose: PropTypes.func,
    isInline: PropTypes.bool.isRequired,
    linksColor: PropTypes.string.isRequired,
    linksColorHover: PropTypes.string.isRequired,
    linksColorFocus: PropTypes.string.isRequired,
};

FormView.defaultProps = {
    backgroundColor: null,
    backgroundImageUrl: null,
    onRequestClose: null,
    displayMode: DISPLAY_MODE_STANDARD,
};

const mapStateToProps = (state, props) => {
    const { formId } = props;

    const formViewStyles = getFormColors(state, formId);
    const { url: backgroundImageUrl } = getFormBackgroundImage(state, formId);
    const { displayMode } = getFormDisplayStyles(state, formId);
    const { default: linksColor, hover: linksColorHover, focus: linksColorFocus } = getFormLinksStyles(state, formId);

    return {
        backgroundColor: formViewStyles.background,
        backgroundImageUrl,
        displayMode,
        linksColor: get(linksColor, 'value', DEFAULT_FORM_LINKS_COLOR),
        linksColorHover: get(linksColorHover, 'value', DEFAULT_FORM_LINKS_COLOR_HOVER),
        linksColorFocus: get(linksColorFocus, 'value', DEFAULT_FORM_LINKS_COLOR_FOCUS),
    };
};

export default withFormId(connect(mapStateToProps, {})(FormView));
